/*
 * jQuery + Popper + Bootstrap 4, self-hosted via npm and bundled by Vite
 * instead of the deferred CDN <script>s that used to live at the end of
 * <body> (includes/scripts.blade.php).
 *
 * Order matters: Bootstrap 4's plugins attach to jQuery's prototype and read a
 * global `Popper`, so jQuery and Popper must be on `window` before the
 * `bootstrap` side-effect import runs. ES module imports are hoisted and run
 * before any statement in this file, so the assignments below can't happen
 * early enough — instead we import jQuery/Popper here (initialising them),
 * publish them to `window`, and pull Bootstrap in via a dynamic import so it
 * evaluates strictly after the globals are set.
 *
 * whenJQuery() in utils/helpers.ts still gates jQuery consumers; this module is
 * imported first in app.ts, so `window.jQuery` is set synchronously during the
 * app entry's evaluation — before any component mounts.
 */
import $ from "jquery";
import Popper from "popper.js";
import Cookies from "js-cookie";

(window as any).$ = (window as any).jQuery = $;
(window as any).Popper = Popper;

/*
 * Resmark's booking/cart widget (resmark-order.js → resmark-widget.js) runs
 * against this page's global jQuery and calls the legacy `jquery.cookie` plugin
 * (`$.cookie(...)`). The old CDN setup loaded that plugin; the self-hosted
 * bundle doesn't, so the widget threw `TypeError: $.cookie is not a function`
 * mid-init and the Book Now / cart buttons silently never wired up (the script
 * itself loads fine, so there's no onerror to catch it). Re-provide a
 * jquery.cookie-compatible API backed by js-cookie, before any widget runs.
 */
const jqueryCookie = function (key?: string, value?: unknown, options?: Cookies.CookieAttributes) {
    // $.cookie() → every cookie; $.cookie(key) → read one.
    if (key === undefined) return Cookies.get();
    if (arguments.length === 1 || value === undefined) return Cookies.get(key);

    // jquery.cookie removes a cookie when the value is null.
    if (value === null) {
        Cookies.remove(key, options);
        return;
    }

    // jquery.cookie's `expires` (days or Date) matches js-cookie's option.
    return Cookies.set(key, String(value), options);
};

($ as any).cookie = jqueryCookie;
($ as any).removeCookie = function (key: string, options?: Cookies.CookieAttributes) {
    Cookies.remove(key, options);
    return !Cookies.get(key);
};

// Bootstrap 4 registers its jQuery plugins as an import side effect.
import("bootstrap");
